/**
 * @description Renders the Personnel Sync Form section of the page
 */

import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';

import ErrorBoundary from '@seccon/shared/ui/components/ErrorBoundary';

import SyncExistingSubjectsViewer from './SyncExistingSubjectsViewer'
import SyncMapField from './SyncMapField';
import SsnSyncField from './SsnSyncField';
import SyncFileField from './SyncFileField';
import useMapList from '../hooks/useMapList';
import FormFooter from './FormFooter';
import useSubmit from '../hooks/useSubmit';
import useExistingPersonnel from '../hooks/useExistingPersonnel';

const styles = {
  formContainer: {
    width: '75%'
  }
};

const SyncForm = () => {
  // Retrieve the list of maps for the user to choose from
  const mapList = useMapList();
  const {
    existingPersonnel, isLoading: isExistingLoading, checkForExistingPersonnel, setExistingPersonnel
  } = useExistingPersonnel();
  // Setup the form
  const form = useForm();
  const { isDirty, isValid } = form.formState;

  // Retrieve the handleSubmit handler and submitting state
  const { handleSubmit, isSubmitting } = useSubmit(form, setExistingPersonnel);

  /**
   * Handles cancel events by redirecting the user back to the personnel list page
   */
  function handleCancel () {
    // If the user selected a facility, send them back to the facility page
    const facilityId = (new URLSearchParams(window.location.search)).get('facilityId') ?? '';

    window.location.href = `/personnel/list?facilityId=${ facilityId }`;
  }

  // Set the form to loading if the map list is not loaded yet
  const isLoading = mapList === null;

  // Disable the submit button if the form is still loading, isn't used, isn't valid, or is submitting
  const disableSubmit = isLoading || !isDirty || !isValid || isSubmitting;

  return (
    <>
      <Stack spacing={3} sx={styles.formContainer}>
        <FormProvider {...form}>
          {isLoading ? (
            <Skeleton animation='wave' variant='rectangular' height={56} />
          ) : (
            <>
              <ErrorBoundary>
                <SyncMapField mapList={mapList} />
              </ErrorBoundary>

              <ErrorBoundary>
                <SsnSyncField
                  onSyncOnSsnChange={checkForExistingPersonnel}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <SyncFileField
                  onFileAdd={checkForExistingPersonnel}
                  setExistingPersonnel={setExistingPersonnel}
                />
              </ErrorBoundary>

              <ErrorBoundary>
                <SyncExistingSubjectsViewer
                  isSubmitting={isSubmitting}
                  isLoading={isExistingLoading}
                  existingPersonnel={existingPersonnel}
                />
              </ErrorBoundary>
            </>
          )}
        </FormProvider>
      </Stack>
      <FormFooter
        disabled={disableSubmit}
        isSubmitting={isSubmitting}
        onBack={() => handleCancel()}
        onCancel={() => handleCancel()}
        onSubmit={form.handleSubmit(handleSubmit)}
        setExistingPersonnel={setExistingPersonnel}
      />
    </>
  );
};

export default SyncForm;
